import React, { useState, useEffect } from 'react';
import './Calculator.css';
import { FaWindowMaximize, FaDoorOpen, FaRulerVertical, FaRulerHorizontal, FaCubes, FaSortNumericUp, FaAngleDown } from 'react-icons/fa';
import { Heading } from '../../../components';

const CostCalculator = () => {
    // Define base prices for windows and doors
    const basePrices = {
        casement: 200,
        doubleHung: 180,
        sliding: 150,
        bay: 250,
        awning: 220,
        picture: 200,
        entry: 300,
        french: 400,
        patio: 350,
        slidingDoor: 330,
        biFold: 500,
        storm: 250
    };

    // Define material costs per square inch
    const materialCosts = {
        vinyl: 0.05,
        wood: 0.08,
        aluminum: 0.06,
        fiberglass: 0.07,
        composite: 0.09
    };

    // State for form inputs
    const [formData, setFormData] = useState({
        windowType: '',
        doorType: '',
        height: '',
        width: '',
        material: '',
        quantity: ''
    });

    const [services, setServices] = useState({
        installation: false,
        removal: false,
        customization: false
    });

    const [totalCost, setTotalCost] = useState('0.00');

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle checkbox changes
    const handleServiceChange = (e) => {
        const { name, checked } = e.target;
        setServices(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    // Disable door type if window type is selected and vice versa
    useEffect(() => {
        if (formData.windowType) {
            setFormData(prev => ({ ...prev, doorType: '' }));
        }
        if (formData.doorType) {
            setFormData(prev => ({ ...prev, windowType: '' }));
        }
        calculateTotalCost();
    }, [formData.windowType, formData.doorType]);

    // Calculate cost whenever inputs change
    useEffect(() => {
        calculateTotalCost();
    }, [formData, services]);

    const calculateTotalCost = () => {
        const { windowType, doorType, height, width, material, quantity } = formData;
        
        // Parse numeric values
        const heightNum = parseInt(height) || 0;
        const widthNum = parseInt(width) || 0;
        const quantityNum = parseInt(quantity) || 0;

        // Validate required fields
        if ((!windowType && !doorType) || !material || heightNum <= 0 || widthNum <= 0 || quantityNum <= 0) {
            setTotalCost('0.00');
            return;
        }

        // Calculate material cost based on area
        const area = heightNum * widthNum;
        const materialCost = materialCosts[material] * area;

        // Calculate total base price
        let totalBasePrice = 0;
        if (windowType) {
            totalBasePrice += basePrices[windowType] * quantityNum;
        }
        if (doorType) {
            totalBasePrice += basePrices[doorType] * quantityNum;
        }

        // Calculate total cost including material and services
        let totalCost = totalBasePrice + materialCost * quantityNum;

        // Add service costs
        if (services.installation) totalCost += 100;
        if (services.removal) totalCost += 50;
        if (services.customization) totalCost += 75;

        // Update total cost
        setTotalCost(totalCost.toFixed(2));
    };

    return (
        <section className="custom-form cost-calculator">
            <div className="box-container"> 
                <div className="content"> 
                    <form method="post" className="calculator-form dark-form"> 

                        {/* Form Heading */}
                        <Heading title={'Cost Calculator'} subTitle={`Calculate Your Service Costs`} linear sideHeading />

                        <div className="input-box">
                            {/* Window Type Select Field */}
                            <div className="select">
                                <div className="select-container">
                                    <label htmlFor="windowType"><FaWindowMaximize /></label>
                                    <select 
                                        className="select-box" 
                                        id="windowType" 
                                        name="windowType" 
                                        value={formData.windowType}
                                        onChange={handleInputChange}
                                        disabled={!!formData.doorType}
                                    >
                                        <option value="">Window Type</option>
                                        <option value="casement">Casement</option>
                                        <option value="doubleHung">Double-Hung</option>
                                        <option value="sliding">Sliding</option>
                                        <option value="bay">Bay</option>
                                        <option value="awning">Awning</option>
                                        <option value="picture">Picture</option>
                                    </select>
                                    <div className="icon-container">
                                        <FaAngleDown className="icon" />
                                    </div>
                                </div>
                            </div>
                            
                            {/* Door Type Select Field */}
                            <div className="select">
                                <div className="select-container">
                                    <label htmlFor="doorType"><FaDoorOpen /></label>
                                    <select 
                                        className="select-box" 
                                        id="doorType" 
                                        name="doorType" 
                                        value={formData.doorType}
                                        onChange={handleInputChange}
                                        disabled={!!formData.windowType}
                                    >
                                        <option value="">Door Type</option>
                                        <option value="entry">Entry</option>
                                        <option value="french">French</option>
                                        <option value="patio">Patio</option>
                                        <option value="sliding">Sliding</option>
                                        <option value="biFold">Bi-Fold</option>
                                        <option value="storm">Storm</option>
                                    </select>
                                    <div className="icon-container">
                                        <FaAngleDown className="icon" />
                                    </div>
                                </div>
                            </div>
                        </div> 
                        
                        <div className="input-box">
                            {/* Height Input Field */}
                            <div className="input-field">
                                <label htmlFor="height"><FaRulerVertical /></label>
                                <input 
                                    type="number" 
                                    name="height" 
                                    className="box" 
                                    id="height" 
                                    min="0" 
                                    placeholder="Height in Inches" 
                                    value={formData.height}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            
                            {/* Width Input Field */}
                            <div className="input-field">
                                <label htmlFor="width"><FaRulerHorizontal /></label>
                                <input 
                                    type="number" 
                                    name="width" 
                                    className="box" 
                                    id="width" 
                                    min="0" 
                                    placeholder="Width in Inches" 
                                    value={formData.width}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>
                        
                        <div className="input-box">
                            {/* Material Select Field */}
                            <div className="select">
                                <div className="select-container">
                                    <label htmlFor="material"><FaCubes /></label>
                                    <select 
                                        className="select-box" 
                                        id="material" 
                                        name="material" 
                                        value={formData.material}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Material</option>
                                        <option value="vinyl">Vinyl</option>
                                        <option value="wood">Wood</option>
                                        <option value="aluminum">Aluminum</option>
                                        <option value="fiberglass">Fiberglass</option>
                                        <option value="composite">Composite</option>
                                    </select>
                                    <div className="icon-container">
                                        <FaAngleDown className="icon" />
                                    </div>
                                </div>
                            </div>
                            
                            {/* Quantity Input Field */}
                            <div className="input-field">
                                <label htmlFor="quantity"><FaSortNumericUp /></label>
                                <input 
                                    type="number" 
                                    name="quantity" 
                                    className="box" 
                                    id="quantity" 
                                    min="0" 
                                    placeholder="Quantity" 
                                    value={formData.quantity}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>
                        
                        {/* Extra Services Checkboxes */}
                        <div className="extra-services">
                            {/* Installation Checkbox */}
                            <div className="checkbox-label">
                                <input 
                                    type="checkbox" 
                                    id="installation-checkbox" 
                                    name="installation"
                                    checked={services.installation}
                                    onChange={handleServiceChange}
                                />
                                <label htmlFor="installation-checkbox">Installation</label>
                                <div className="checkbox-indicator"></div>
                            </div>
                            
                            {/* Removal of Old Checkbox */}
                            <div className="checkbox-label">
                                <input 
                                    type="checkbox" 
                                    id="removal-checkbox" 
                                    name="removal"
                                    checked={services.removal}
                                    onChange={handleServiceChange}
                                />
                                <label htmlFor="removal-checkbox">Removal of Old</label>
                                <div className="checkbox-indicator"></div>
                            </div>
                            
                            {/* Customization Checkbox */}
                            <div className="checkbox-label">
                                <input 
                                    type="checkbox" 
                                    id="customization-checkbox" 
                                    name="customization"
                                    checked={services.customization}
                                    onChange={handleServiceChange}
                                />
                                <label htmlFor="customization-checkbox">Customization</label>
                                <div className="checkbox-indicator"></div>
                            </div>
                        </div>
                        
                        {/* Total Cost Display */}
                        <div className="cost-total">
                            <h5>total</h5>
                            <span>${totalCost}</span>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
};

export default CostCalculator;